import React from "react";
import { useLocation } from "react-router-dom";

/**
 * RouteErrorBoundary — catches render-time errors in the routed page subtree so
 * a single broken page shows a visible error instead of unmounting the whole
 * React root (the dreaded blank white screen). The app shell (navbar/sidebar)
 * stays alive because this boundary sits *inside* AppLayout.
 */
class RouteErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to the dev console with the component stack for debugging.
    // eslint-disable-next-line no-console
    console.error("RouteErrorBoundary caught:", error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger" role="alert">
          <h5 className="alert-heading">Ocurrió un error al mostrar esta página</h5>
          <p className="mb-2">
            El resto de la aplicación sigue funcionando. Detalle técnico:
          </p>
          <pre
            className="mb-3 p-2 bg-light border rounded"
            style={{ whiteSpace: "pre-wrap", fontSize: "0.8rem", maxHeight: 240, overflow: "auto" }}
          >
            {String(error?.stack || error?.message || error)}
          </pre>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            style={{ minHeight: 44 }}
            onClick={this.handleReset}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }
}

/**
 * Functional wrapper that remounts the boundary on every route change (keyed by
 * pathname), so navigating to a working page clears a previous page's error
 * without the user having to press "Reintentar".
 */
function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <RouteErrorBoundaryInner key={pathname}>{children}</RouteErrorBoundaryInner>;
}

export { RouteErrorBoundary };
